import React, { useState } from 'react';
import type { TableColumnsType } from 'antd';
import { Button, Table } from 'antd';
import { OrderType } from '../../../graphql/types/OrderType';
import { useStatusInternationalization } from '../../delivery/parcels/hooks/useStatusInternationalization';
import { formatTimestampToDateTime, formatISODateTime } from '../../../utils/dateTimeUtils';
import { t } from '../../../i18n';
import CloseOrderModal from './CloseOrderModal';
import { useQuery } from '@apollo/client';
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../graphql/queries/getChannelAll';
import { useDspOptions } from '../../../hooks/useDspOptions';
import {
    ExceptionDictionaryResponse,
    GET_ALL_EXCEPTION
} from '../../delivery/parcels/graphql/queries/getAllException';

interface OrderCloseColumnsProps {
    parcelData: OrderType[];
    showParcelDetail: (record: OrderType) => void;
    onSelectChange?: (selectedRowKeys: React.Key[], selectedRows: OrderType[]) => void;
    selectedRowKeys?: React.Key[];
    loading?: boolean;
    onCloseOrder?: (record: OrderType) => void;
}

export const OrderCloseColumns: React.FC<OrderCloseColumnsProps> = ({
    parcelData,
    showParcelDetail,
    onSelectChange,
    selectedRowKeys: externalSelectedRowKeys,
    loading,
    onCloseOrder
}) => {
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<React.Key[]>([]);
    const [closeModalVisible, setCloseModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<OrderType | null>(null);
    const selectedRowKeys = externalSelectedRowKeys !== undefined ? externalSelectedRowKeys : internalSelectedRowKeys;

    const { data } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const { dspOptions } = useDspOptions();

    const {
        getStatusText,
        getExceptionReasonText,
        getNextActionTextFromBackend,
        getExceptionStatusText,
        getExceptionText
    } = useStatusInternationalization();

    const handleSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: OrderType[]) => {
        if (onSelectChange) {
            onSelectChange(newSelectedRowKeys, selectedRows);
        } else {
            setInternalSelectedRowKeys(newSelectedRowKeys);
        }
    };

    const handleCloseOrder = (record: OrderType) => {
        if (onCloseOrder) {
            onCloseOrder(record);
        } else {
            setSelectedOrder(record);
            setCloseModalVisible(true);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
    };


    const columns: TableColumnsType<OrderType> = [
        {
            title: t('central.parcels.columns.tracking'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'center',
            fixed: 'left',
            width: 150,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showParcelDetail(record)}
                    className="p-0"
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t('central.parcels.columns.order'),
            dataIndex: 'channelOrderNumber',
            key: 'channelOrderNumber',
            align: 'center',
            width: 150,
        },
        {
            title: t('central.parcels.columns.client'),
            key: 'clientCode',
            align: 'center',
            width: 120,
            render: (_, record) => record.channel?.channelName || record.channel?.clientCode || '-',
        },
        {
            title: t('central.parcels.columns.status'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            align: 'center',
            width: 120,
            render: (status) => getStatusText(status),
        },
        {
            title: t('central.parcels.columns.sortCode1'),
            key: 'sortingCodeSegment1',
            align: 'center',
            width: 100,
            render: (_, record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[0] || '-';
            },
        },
        {
            title: t('central.parcels.columns.sortCode2'),
            key: 'sortingCodeSegment2',
            align: 'center',
            width: 100,
            render: (_, record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[1] || '-';
            },
        },
        {
            title: t('central.parcels.columns.sortCode3'),
            key: 'sortingCodeSegment3',
            align: 'center',
            width: 100,
            render: (_, record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[2] || '-';
            },
        },
        {
            title: t('delivery.parcels.label.dsp'),
            dataIndex: 'deliveredDspId',
            key: 'deliveredDspId',
            align: 'center',
            width: 120,
            render: (dspId) => {
                if (!dspId) return '-';
                const dsp = dspOptions?.find(option =>
                    String(option.value) === String(dspId)
                );
                return dsp ? dsp.label : '-';
            },
        },
        {
            title: t('central.parcels.filters.pickupTaskDate'),
            dataIndex: 'receivedAtHub',
            key: 'receivedAtHub',
            align: 'center',
            width: 180,
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('central.parcels.filters.whsReceiveDate'),
            dataIndex: 'destWarehouseArrivedAt',
            key: 'destWarehouseArrivedAt',
            align: 'center',
            width: 180,
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('central.parcels.filters.lastDeliveryScanTime'),
            dataIndex: 'lastDeliveryScanTime',
            key: 'lastDeliveryScanTime',
            align: 'center',
            width: 180,
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('central.parcels.columns.hasException'),
            dataIndex: 'isHasBeenSetException',
            key: 'isHasBeenSetException',
            align: 'center',
            width: 100,
            render: (value) => getExceptionText(value),
        },
        {
            title: t('central.parcels.columns.exceptionStatus'),
            dataIndex: 'isAbnormal',
            key: 'isAbnormal',
            align: 'center',
            width: 100,
            render: (text, record) => {
                if (!record.isHasBeenSetException) {
                    return '-';
                }
                return getExceptionStatusText(text, record.exceptionStatus);
            }
        },
        {
            title: t('central.parcels.columns.lastExceptionReason'),
            dataIndex: 'exceptionReasonCode',
            key: 'exceptionReasonCode',
            align: 'center',
            width: 150,
            render: (text, record) => {
                const exception = exceptionList?.find(
                    (exception) => String(exception.exceptionReasonCode) === String(record.exceptionReasonCode)
                );
                return getExceptionReasonText(exception, record.exceptionReasonCode);
            }
        },
        {
            title: t('central.parcels.columns.lastExceptionDate'),
            dataIndex: 'markExceptionAt',
            key: 'markExceptionAt',
            align: 'center',
            width: 180,
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('central.parcels.columns.nextAction'),
            dataIndex: 'nextAction',
            key: 'nextAction',
            align: 'center',
            width: 120,
            render: (value) => getNextActionTextFromBackend(value),
        },
        {
            title: t('delivery.parcels.label.lastAssignedDriver'),
            dataIndex: 'lastAssignedDriverName',
            key: 'lastAssignedDriverName',
            align: 'center',
            width: 150,
            render: (text) => text || '-',
        },
        {
            title: t('orderClose.fields.daysFromAscan'),
            key: 'daysFromAscan',
            align: 'center',
            width: 120,
            render: (_, record) => {
                // 优先使用后端返回的计算值
                if ((record as any).daysFromAscan !== undefined) {
                    return (record as any).daysFromAscan;
                }
                // 如果没有，则前端计算（使用 receivedAtHub，对应 PickupArrivalScan）
                if (!record.receivedAtHub) return '-';
                const ascanTimestamp = typeof record.receivedAtHub === 'string'
                    ? parseInt(record.receivedAtHub)
                    : record.receivedAtHub;
                const ascanTime = new Date(ascanTimestamp * 1000).getTime();
                const now = Date.now();
                const days = Math.floor((now - ascanTime) / (1000 * 60 * 60 * 24));
                return days;
            },
        },
        {
            title: t('orderClose.fields.hoursToClose'),
            key: 'hoursToClose',
            align: 'center',
            width: 150,
            render: (_, record) => {
                // 优先使用后端返回的计算值
                if ((record as any).hoursToClose !== undefined) {
                    const hoursToClose = (record as any).hoursToClose;
                    // 允许显示负数，与下载结果保持一致
                    return hoursToClose ?? '-';
                }
                // 如果没有，则前端计算（使用 receivedAtHub，对应 PickupArrivalScan）
                if (!record.receivedAtHub) return '-';
                const ascanTimestamp = typeof record.receivedAtHub === 'string'
                    ? parseInt(record.receivedAtHub)
                    : record.receivedAtHub;
                const ascanTime = new Date(ascanTimestamp * 1000).getTime();
                const now = Date.now();
                const hoursFromAscan = Math.floor((now - ascanTime) / (1000 * 60 * 60));
                const clientCode = record.channel?.clientCode || '';
                const isTemu = clientCode?.toUpperCase().startsWith('TEMU');
                const totalHours = isTemu ? 360 : 336;
                const hoursToClose = totalHours - hoursFromAscan;
                // 允许显示负数，与下载结果保持一致
                return hoursToClose;
            },
        },
        {
            title: t('orderClose.fields.flowType'),
            dataIndex: 'flowType',
            key: 'flowType',
            align: 'center',
            width: 120,
            render: (value: string | undefined) => {
                if (!value) return '-';
                return t(`orderClose.flowType.${value}`);
            },
        },
        {
            title: t('orderClose.fields.closeTime'),
            key: 'closeTime',
            align: 'center',
            width: 180,
            render: (_, record) => {
                const closeRecord = (record as any).latestCloseRecord;
                if (!closeRecord?.closeTime) return '-';
                // 后端返回的是 ISO 8601 格式的 UTC 时间字符串（如 "2024-01-15T10:30:00Z"）
                // 使用 formatISODateTime 可以正确解析并转换为浏览器本地时区显示
                const closeTimeStr = String(closeRecord.closeTime);
                // 检查是否是 ISO 8601 格式（包含 'T' 或 'Z'）
                if (closeTimeStr.includes('T') || closeTimeStr.includes('Z')) {
                    return formatISODateTime(closeTimeStr);
                }
                // 否则按时间戳处理
                return formatTimestampToDateTime(closeTimeStr);
            },
        },
        {
            title: t('orderClose.fields.closeOperator'),
            key: 'closeOperator',
            align: 'center',
            width: 150,
            render: (_, record) => {
                const closeRecord = (record as any).latestCloseRecord;
                if (!closeRecord?.closeOperatorAccountId) return '-';
                const accountId = closeRecord.closeOperatorAccountId;
                const accountName = closeRecord.closeOperatorAccountName;
                // 同时显示名称和ID
                if (accountName) {
                    return `${accountName} (${accountId})`;
                }
                return accountId;
            },
        },
    ];

    return (
        <>
            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={parcelData}
                loading={loading}
                rowKey={(record) => record.id?.toString() || ''}
                pagination={false}
                scroll={{ x: 'max-content' }}
            />
            {selectedOrder && (
                <CloseOrderModal
                    visible={closeModalVisible}
                    onClose={() => {
                        setCloseModalVisible(false);
                        setSelectedOrder(null);
                    }}
                    order={selectedOrder}
                    onSuccess={() => {
                        setCloseModalVisible(false);
                        setSelectedOrder(null);
                        // 刷新列表
                        window.location.reload();
                    }}
                />
            )}
        </>
    );
};
